import { useEffect, useState } from 'react';
import WebApp from '@twa-dev/sdk';
import { Package, ChevronDown } from 'lucide-react';
import { api } from '../api/api';
import { Order } from '../types';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

const ORDER_STATUS_RU: Record<string, string> = {
  PENDING: 'Ожидает',
  CONFIRMED: 'Подтвержден',
  PROCESSING: 'В обработке',
  SHIPPED: 'Отправлен',
  DELIVERED: 'Доставлен',
  CANCELLED: 'Отменен',
};

const STATUS_COLORS: Record<string, string> = {
  PENDING: 'bg-orange-500',
  CONFIRMED: 'bg-blue-500',
  PROCESSING: 'bg-purple-500',
  SHIPPED: 'bg-cyan-500',
  DELIVERED: 'bg-green-500',
  CANCELLED: 'bg-red-500',
};

export function OrdersPage() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedOrder, setExpandedOrder] = useState<number | null>(null);

  const userId = WebApp.initDataUnsafe.user?.id?.toString() || '0';

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const data = await api.getOrders(userId);
      setOrders(data);
    } catch (err) {
      setError('Не удалось загрузить заказы');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px] text-muted-foreground">
        Загрузка...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 m-4 bg-destructive/10 text-destructive rounded-lg">
        {error}
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="min-h-screen pb-20">
        <header className="sticky top-0 z-10 bg-background border-b px-4 py-5">
          <h1 className="text-2xl font-bold">Мои заказы</h1>
        </header>
        <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
          <Package className="w-20 h-20 text-muted-foreground mb-4" />
          <h2 className="text-xl font-semibold mb-2">У вас пока нет заказов</h2>
          <p className="text-muted-foreground">Ваши заказы появятся здесь после оформления</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-20">
      <header className="sticky top-0 z-10 bg-background border-b px-4 py-5 flex justify-between items-center">
        <h1 className="text-2xl font-bold">Мои заказы</h1>
        <span className="bg-primary text-primary-foreground text-sm font-medium px-2.5 py-0.5 rounded-full">
          {orders.length}
        </span>
      </header>

      <div className="p-4 space-y-3">
        {orders.map(order => {
          const isExpanded = expandedOrder === order.id;
          const date = new Date(order.createdAt).toLocaleDateString('ru-RU', {
            day: 'numeric',
            month: 'short',
          });

          return (
            <Card key={order.id} className="overflow-hidden">
              <div
                className="p-4 cursor-pointer"
                onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold">Заказ #{order.id}</div>
                    <div className="text-sm text-muted-foreground">{date}</div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="font-bold">{order.totalAmount} ₽</div>
                      <span className={cn(
                        "inline-block text-xs text-white px-2 py-0.5 rounded-full",
                        STATUS_COLORS[order.status]
                      )}>
                        {ORDER_STATUS_RU[order.status]}
                      </span>
                    </div>
                    <ChevronDown className={cn(
                      "h-5 w-5 text-muted-foreground transition-transform",
                      isExpanded && "rotate-180"
                    )} />
                  </div>
                </div>
              </div>

              {isExpanded && (
                <CardContent className="border-t pt-4 space-y-4">
                  <div>
                    <h3 className="text-sm font-semibold mb-2">Состав заказа:</h3>
                    <div className="space-y-2">
                      {order.items.map(item => (
                        <div key={item.id} className="flex justify-between text-sm">
                          <span className="text-muted-foreground">
                            {item.product.name} × {item.quantity}
                          </span>
                          <span className="font-medium">
                            {(parseFloat(item.price) * item.quantity).toFixed(0)} ₽
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {order.deliveryAddress && (
                    <div>
                      <div className="text-sm font-semibold">Адрес доставки:</div>
                      <p className="text-sm text-muted-foreground">{order.deliveryAddress}</p>
                    </div>
                  )}

                  {order.phone && (
                    <div>
                      <div className="text-sm font-semibold">Телефон:</div>
                      <p className="text-sm text-muted-foreground">{order.phone}</p>
                    </div>
                  )}

                  {order.comment && (
                    <div>
                      <div className="text-sm font-semibold">Комментарий:</div>
                      <p className="text-sm text-muted-foreground">{order.comment}</p>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}
